import type { OrderDetails } from '../model'
import type { ProofOfAgePayload } from '@/features/order/model'
import { isTutorialModeActive, withTutorialFallback } from '@/features/onboarding/lib'
import { apiFetch } from '@/shared/api'

interface PersistZebraEventBase {
  orderId: string
  crateType: string
  deviceFingerprint?: string
  timestamp: string
}

export interface PersistZebraScanParams extends PersistZebraEventBase {
  eventType: 'zebraScans'
  barcode: string
  areaCode: string
  crateNumber: string
}

export interface PersistMissingCrateParams extends PersistZebraEventBase {
  eventType: 'missingCrates'
  areaCode: string
  reason: string
  comment?: string
}

export type PersistZebraEventParams = PersistZebraScanParams | PersistMissingCrateParams

/**
 * Fetches all orders for the current store/session straight from the API.
 * Never returns tutorial mock data — use for stores that must only ever hold real orders.
 */
export async function fetchOrdersFromApi() {
  return apiFetch<OrderDetails[]>('/orders')
}

/**
 * Fetches all orders for the current store/session.
 * If tutorial mode is active, returns mock data instead of making API call.
 */
export async function fetchOrders() {
  return withTutorialFallback<OrderDetails[]>(
    'orders',
    fetchOrdersFromApi,
  )
}

/**
 * Fetches wait time information from the API using the application's apiFetch utility.
 * If tutorial mode is active, returns mock data instead of making API call.
 * @param timezoneOffset - The client's timezone offset in minutes.
 */
export async function getWaitTime(
  timezoneOffset?: number,
) {
  return await withTutorialFallback<{ waitTimeAverage: number, numberOfOrders: number }>(
    'waitTime',
    async () => {
      let endpoint = '/orders/waitTime'

      // Append timezoneOffset query parameter if it's a valid number
      if (typeof timezoneOffset === 'number' && !Number.isNaN(timezoneOffset)) {
        endpoint += `?timezoneOffset=${timezoneOffset}`
      }

      return apiFetch<{
        waitTimeAverage: number
        numberOfOrders: number
      }>(endpoint)
    },
  )
}

/**
 * Updates the status of an order.
 *
 * @param params - The parameters for updating an order status
 * @param params.orderId - The ID of the order to update
 * @param params.newStatus - The new status to set
 * @param params.lastUpdateTime - Optional last update time for optimistic concurrency control
 * @param params.verificationData - Optional verification data for verified status changes
 * @param params.verificationData.verificationCode - Optional verification code for verified status changes
 * @param params.verificationData.verificationErrorCode - Optional error code when verification is not possible
 * @param params.verificationData.exceptionReason - Optional reason when verification is not possible
 * @param params.proofOfAge - Optional Proof of Age record for a restricted order (BD-8253)
 * @returns The updated order details
 */
export async function updateOrderStatus<T extends OrderDetails>({
  orderId,
  newStatus,
  lastUpdateTime,
  verificationData,
  proofOfAge,
}: {
  orderId: string
  newStatus: string
  lastUpdateTime?: string
  verificationData?: {
    verificationCode?: string
    verificationErrorCode?: string
    exceptionReason?: string
  }
  proofOfAge?: ProofOfAgePayload
}) {
  // Tutorial orders are mock data: never send their ids to the real backend.
  if (isTutorialModeActive()) {
    return {} as T
  }

  const url = `/orders/${orderId}`
  // Keep verification data on the same status patch object.
  const statusPatch: {
    op: 'replace'
    path: '/status'
    value: string
    verificationCode?: string
    exceptionReason?: string
    proofOfAge?: ProofOfAgePayload
  } = {
    op: 'replace',
    path: '/status',
    value: newStatus,
  }

  if (verificationData) {
    if (verificationData.verificationCode) {
      statusPatch.verificationCode = verificationData.verificationCode
    }

    if (verificationData.exceptionReason) {
      statusPatch.exceptionReason = verificationData.exceptionReason
    }
  }

  // BD-8253: the audit record rides on the status patch so the completion and the
  // record land together. Sending them as separate requests would allow a restricted
  // order to reach Completed with no record attached if the second call failed.
  if (proofOfAge) {
    statusPatch.proofOfAge = proofOfAge
  }

  const patches: object[] = [statusPatch]

  return apiFetch<T>(url, {
    method: 'PATCH',
    body: patches,
    headers: lastUpdateTime ? {
      'x-bluedot-last-update-time': lastUpdateTime,
    } : undefined,
  })
}

/**
 * Persists a Zebra scan or missing-crate event immediately.
 * eventType 'zebraScans' — individual barcode scan; idempotent by barcode.
 * eventType 'missingCrates' — mark-as-missing submission; idempotent by crateType.
 * The backend is idempotent for both types: duplicate submissions are silently ignored.
 */
export async function persistZebraEvent({
  orderId,
  ...body
}: PersistZebraEventParams): Promise<OrderDetails | undefined> {
  if (isTutorialModeActive()) {
    return undefined
  }

  return apiFetch<OrderDetails>(`/orders/${orderId}/zscans`, {
    method: 'POST',
    body,
  })
}

/**
 * Writes the team member initials captured after completion against an order.
 *
 * Uses a dedicated `/tmInitials` patch (no `/status` change) so the backend does
 * not recompute wait time or re-trigger positive feedback. The stored order field
 * is `tm_initials` (reporting), while the patch path follows the API camelCase
 * convention.
 *
 * @param params - The parameters for writing initials
 * @param params.orderId - The order to write initials against
 * @param params.tmInitials - The captured initials (e.g. "MR")
 * @returns The updated order details
 */
export async function updateOrderTmInitials<T extends OrderDetails>({
  orderId,
  tmInitials,
}: {
  orderId: string
  tmInitials: string
}): Promise<T> {
  if (isTutorialModeActive()) {
    return {} as T
  }

  const url = `/orders/${orderId}`
  const patches = [
    {
      op: 'replace',
      path: '/tmInitials',
      value: tmInitials,
    },
  ]

  return apiFetch<T>(url, {
    method: 'PATCH',
    body: patches,
  })
}

/**
 * Acknowledges an order by setting isAcknowledged to true
 * @param params - The parameters for acknowledging an order
 * @param params.orderId - The ID of the order to acknowledge
 * @param params.lastUpdateTime - Optional last update time for optimistic concurrency control
 * @returns The updated order details
 */
export async function updateAcknowledgeOrder<T extends OrderDetails>({
  orderId,
  lastUpdateTime,
}: {
  orderId: string
  lastUpdateTime?: string
}): Promise<T> {
  if (isTutorialModeActive()) {
    return {} as T
  }

  const url = `/orders/${orderId}`
  const patches = [
    {
      op: 'replace',
      path: '/isAcknowledged',
      value: true,
    },
  ]

  return apiFetch<T>(url, {
    method: 'PATCH',
    body: patches,
    headers: lastUpdateTime ? {
      'x-bluedot-last-update-time': lastUpdateTime,
    } : undefined,
  })
}
